import React from "react";

import type { OperationsWeek } from "@/api/warehouse_dashboard";
import { sparkPoints } from "@/lib/warehouse-operations";

/**
 * Спарклайн по недельным `values` — inline SVG, без библиотек и без цвета
 * направления. Общий для «Операций» (ячейка таблицы) и плиток «Обзора».
 * `null` рвёт линию; отрезок в текущую неполную неделю — пунктиром: её
 * «падение» — это неполнота, а не спад. `mark` — индекс недели, которую
 * плитка показывает крупной цифрой: точка на ней связывает число и линию.
 */
export function Sparkline({
  values,
  weeks,
  width = 72,
  height = 20,
  mark,
  className = "mx-auto block text-slate-500",
}: {
  values: (number | null)[];
  weeks: OperationsWeek[];
  width?: number;
  height?: number;
  mark?: number | null;
  className?: string;
}): React.ReactElement {
  const pts = sparkPoints(values, width, height);
  const segments: { d: string; dashed: boolean }[] = [];
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    if (!a || !b) continue;
    segments.push({ d: `M${a.x} ${a.y}L${b.x} ${b.y}`, dashed: Boolean(weeks[i]?.partial) });
  }
  // Точка, у которой нет соседей, — иначе одиночное значение между прочерками
  // не видно вовсе.
  const lonely = pts.flatMap((p, i) =>
    p && !pts[i - 1] && !pts[i + 1] && i !== mark ? [{ ...p, key: i }] : [],
  );
  const marked = mark === null || mark === undefined ? null : pts[mark];
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      aria-hidden="true"
    >
      {segments.map((s) => (
        <path
          key={s.d}
          d={s.d}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeDasharray={s.dashed ? "2 2" : undefined}
          opacity={s.dashed ? 0.6 : 1}
        />
      ))}
      {lonely.map((p) => (
        <circle key={p.key} cx={p.x} cy={p.y} r={1.5} fill="currentColor" />
      ))}
      {marked && (
        <circle
          cx={marked.x}
          cy={marked.y}
          r={2.5}
          className="fill-slate-900"
          stroke="white"
          strokeWidth={1}
        />
      )}
    </svg>
  );
}
